import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Breadcrumbs,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  FormHelperText,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LogoutNavbar from '../../components/LogoutNavbar';
import PostLoginFooter from '../../components/PostLoginFooter';
import Sidebar from '../../components/GlobalSidebar';
import api from '../../api';
import '../../styles/AdminDashboard.css';
import '../../styles/Announcements.css';

export default function AdminAnnouncements() {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTabletDown = useMediaQuery(theme.breakpoints.down('md'));

  const [status, setStatus] = useState('loading'); // loading | ready
  const [items, setItems] = useState([]);

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [pinned, setPinned] = useState(false);
  const [audStudents, setAudStudents] = useState(true);
  const [audClub, setAudClub] = useState(true);
  const [audAdmins, setAudAdmins] = useState(true);
  const [expiresAt, setExpiresAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const wantsNew = useMemo(() => searchParams.get('new') === '1', [searchParams]);

  const normalizedTitle = title.trim();
  const normalizedMessage = message.trim();
  const selectedAudience = audStudents || audClub || audAdmins;
  const canCreate = !!normalizedTitle && !!normalizedMessage && selectedAudience && !saving;

  const titleError = submitted && !normalizedTitle;
  const messageError = submitted && !normalizedMessage;
  const audienceError = submitted && !selectedAudience;

  const load = async () => {
    setStatus('loading');
    try {
      const res = await api.get('announcements/manage/');
      const list = Array.isArray(res) ? res : (res?.results || []);
      setItems(list);
    } catch (e) {
      setItems([]);
      enqueueSnackbar('Failed to load announcements.', { variant: 'error' });
    } finally {
      setStatus('ready');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (wantsNew) {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
    }
  }, [wantsNew]);

  const onCreate = async (e) => {
    e.preventDefault();

    setSubmitted(true);

    if (!normalizedTitle || !normalizedMessage) {
      enqueueSnackbar('Title and message are required.', { variant: 'warning' });
      return;
    }

    if (!selectedAudience) {
      enqueueSnackbar('Select at least one audience.', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await api.post('announcements/manage/', {
        title: normalizedTitle,
        message: normalizedMessage,
        pinned,
        audience_students: audStudents,
        audience_club_members: audClub,
        audience_admins: audAdmins,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });

      setTitle('');
      setMessage('');
      setPinned(false);
      setAudStudents(true);
      setAudClub(true);
      setAudAdmins(true);
      setExpiresAt('');
      setSubmitted(false);

      enqueueSnackbar('Announcement created.', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar('Create failed. Please check inputs.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (a) => {
    setDeleteTarget(a);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await api.delete(`announcements/manage/${deleteTarget.id}/`);
      enqueueSnackbar('Announcement deleted.', { variant: 'success' });
      setDeleteTarget(null);
      await load();
    } catch {
      enqueueSnackbar('Delete failed.', { variant: 'error' });
    }
  };

  const sortedItems = useMemo(() => {
    const list = Array.isArray(items) ? [...items] : [];
    list.sort((a, b) => {
      const ap = a?.pinned ? 1 : 0;
      const bp = b?.pinned ? 1 : 0;
      if (ap !== bp) return bp - ap;

      const at = a?.created_at ? Date.parse(a.created_at) : 0;
      const bt = b?.created_at ? Date.parse(b.created_at) : 0;
      return bt - at;
    });
    return list;
  }, [items]);

  const formatDateTime = (value) => {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return '';
    }
  };

  return (
    <div className="admin-dashboard announcements-page" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LogoutNavbar />

      <div className="dashboard-layout" style={{ display: 'flex', flexGrow: 1 }}>
        {!isTabletDown && <Sidebar />}

        <main className="dashboard-main" style={{ flexGrow: 1, overflowX: 'hidden' }}>
          <div className="dashboard-container">
            <Stack spacing={2.25} sx={{ py: isMobile ? 2 : 3 }}>
              <header className="an-page-header">
                <Box className="an-header-left">
                  <Box className="an-breadcrumb-row">
                    <Tooltip title="Back">
                      <IconButton
                        onClick={() => navigate('/admin/dashboard')}
                        className="an-back-icon"
                        size="medium"
                        aria-label="Back"
                      >
                        <ArrowBackIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Breadcrumbs
                      className="an-breadcrumbs"
                      separator={<NavigateNextIcon fontSize="small" />}
                      aria-label="breadcrumb"
                    >
                      <Link
                        underline="hover"
                        color="inherit"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/admin/dashboard');
                        }}
                        href="/admin/dashboard"
                        className="an-breadcrumb-link"
                      >
                        Dashboard
                      </Link>
                      <Typography color="text.primary" className="an-breadcrumb-current">
                        Announcements
                      </Typography>
                    </Breadcrumbs>
                  </Box>

                  <Box className="an-title-wrap">
                    <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" className="an-page-title">
                      Manage Announcements
                    </Typography>
                    <Typography variant="body2" className="an-page-subtitle" color="text.secondary">
                      Announcements are automatically scoped to your college.
                    </Typography>
                  </Box>
                </Box>

                <Box className="an-header-actions">
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/announcements')}
                    className="an-secondary-btn"
                  >
                    View Announcements
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/admin/announcements?new=1')}
                    className="an-primary-btn"
                  >
                    New Announcement
                  </Button>
                </Box>
              </header>

              <Paper className="an-card" elevation={0}>
                <Box className="an-card-header">
                  <Box>
                    <Typography variant="h6" className="an-card-title">
                      New Announcement
                    </Typography>
                    <Typography className="an-card-subtitle">
                      Write a clear update, choose who should see it, and optionally pin or set an expiry.
                    </Typography>
                  </Box>
                </Box>

                <Box className="an-card-body">
                  <Box component="form" onSubmit={onCreate} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Announcement content
                      </Typography>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Title"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          fullWidth
                          size={isMobile ? 'medium' : 'small'}
                          autoFocus={wantsNew && !isMobile}
                          error={titleError}
                          helperText={titleError ? 'Title is required.' : 'Make it action-oriented (what + when).'}
                          placeholder="Maintenance window tonight 9–10 PM"
                        />

                        <TextField
                          label="Message"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          fullWidth
                          multiline
                          minRows={isMobile ? 6 : 5}
                          error={messageError}
                          helperText={
                            messageError
                              ? 'Message is required.'
                              : `${message.length} characters • Include what/who/when + link (optional).`
                          }
                          placeholder={
                            'What: …\nWho: …\nWhen: …\nLink (optional): …'
                          }
                        />
                      </Stack>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
                        Audience
                      </Typography>

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={0.5}
                        sx={{ flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, alignItems: 'center' }}
                      >
                        <FormControlLabel
                          control={<Checkbox checked={audStudents} onChange={(e) => setAudStudents(e.target.checked)} />}
                          label="Students"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={audClub} onChange={(e) => setAudClub(e.target.checked)} />}
                          label="Club Members"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={audAdmins} onChange={(e) => setAudAdmins(e.target.checked)} />}
                          label="Admins"
                        />
                        <FormControlLabel
                          control={<Checkbox checked={pinned} onChange={(e) => setPinned(e.target.checked)} />}
                          label="Pinned (shows first)"
                        />
                      </Stack>
                      {audienceError ? (
                        <FormHelperText error sx={{ mt: 0.5 }}>
                          Select at least one audience.
                        </FormHelperText>
                      ) : (
                        <FormHelperText sx={{ mt: 0.5 }}>
                          Audience controls who sees this in banners and the announcements list.
                        </FormHelperText>
                      )}
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                        Schedule
                      </Typography>
                      <TextField
                        label="Expires At (optional)"
                        type="datetime-local"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size={isMobile ? 'medium' : 'small'}
                        helperText="After expiry, it won’t show in announcement banners."
                        fullWidth
                      />
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                      <Button type="submit" variant="contained" disabled={!canCreate} fullWidth={isMobile}>
                        {saving ? 'Creating…' : 'Create announcement'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>

              <Paper className="an-card" elevation={0}>
                <Box className="an-card-header">
                  <Box>
                    <Typography variant="h6" className="an-card-title">
                      Existing
                    </Typography>
                    <Typography className="an-card-subtitle">{sortedItems.length} total</Typography>
                  </Box>
                </Box>

                <Box className="an-card-body">
                  {status === 'ready' && sortedItems.length === 0 ? (
                    <Typography variant="body2">No announcements yet.</Typography>
                  ) : (
                    <Stack spacing={2}>
                      {sortedItems.map((a) => {
                        const created = formatDateTime(a.created_at);
                        const expires = formatDateTime(a.expires_at);

                        return (
                          <Paper key={a.id} sx={{ p: 2, borderRadius: 2 }}>
                            <Stack spacing={1}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 900 }} noWrap>
                                    {a.title}
                                  </Typography>

                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75, alignItems: 'center' }}
                                  >
                                    {a.pinned ? <Chip size="small" label="Pinned" /> : null}
                                    {a.audience_students ? <Chip size="small" label="Students" variant="outlined" /> : null}
                                    {a.audience_club_members ? <Chip size="small" label="Club Members" variant="outlined" /> : null}
                                    {a.audience_admins ? <Chip size="small" label="Admins" variant="outlined" /> : null}
                                  </Stack>
                                </Box>

                                <IconButton
                                  aria-label="Delete announcement"
                                  color="error"
                                  onClick={() => requestDelete(a)}
                                  size={isMobile ? 'medium' : 'small'}
                                >
                                  <DeleteOutlineRoundedIcon />
                                </IconButton>
                              </Box>

                              <Divider />

                              <Typography
                                variant="body2"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {a.message}
                              </Typography>

                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} sx={{ opacity: 0.85 }}>
                                {created ? <Typography variant="caption">Posted {created}</Typography> : null}
                                {expires ? (
                                  <Typography variant="caption">Expires {expires}</Typography>
                                ) : (
                                  <Typography variant="caption">No expiry</Typography>
                                )}
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Stack>
          </div>
        </main>
      </div>

      <PostLoginFooter />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete announcement?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete “{deleteTarget?.title || 'this announcement'}”.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} variant="outlined">Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
